import { graphqlRequest } from "../graphqlClient";

// ─── Response types ──────────────────────────────────────────────────────────

export interface BankAccountViewNode {
  id: string;
  name: string;
  bank: string;
  currencyCode: string;
  balance: number;
  category: string | null;
  accountType: string | null;
  externalId: string | null;
  dataSourceId: number | null;
  dataSourceName: string | null;
  bankAccountNumber: string | null;
  sortCode: string | null;
}

export interface RestrictedFundViewNode {
  id: string;
  bankAccountId: number;
  restrictionType: string;
  label: string;
  restrictedAmount: number;
  isLegal: boolean;
  releaseDate: string | null;
  noticePeriodDays: number | null;
}

export interface CreditFacilityViewNode {
  id: string;
  bankAccountId: number;
  type: string;
  facilityLimit: number;
  facilityDrawn: number;
  facilityRate: number | null;
  facilityReviewDate: string | null;
  securedOn: string | null;
  headroom: number;
}

export interface BankAccountViewTotals {
  accountCount: number;
  totalLedgerBalance: number;
  totalRestricted: number;
  totalAvailableCash: number;
  totalHeadroom: number;
}

interface ConnectionEdge<T> {
  cursor: string;
  node: T;
}

interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

interface Connection<T> {
  edges: ConnectionEdge<T>[];
  pageInfo: PageInfo;
}

// ─── Queries ─────────────────────────────────────────────────────────────────

interface BankAccountViewListResponse {
  bankAccounts: {
    bankAccountViewList: Connection<BankAccountViewNode>;
  };
}

export async function getBankAccountsGraphql(
  first?: number,
  after?: string,
): Promise<Connection<BankAccountViewNode>> {
  const query = `
    query BankAccountViewList($first: Int, $after: String) {
      bankAccounts {
        bankAccountViewList(first: $first, after: $after) {
          edges {
            cursor
            node {
              id
              name
              bank
              currencyCode
              balance
              category
              accountType
              externalId
              dataSourceId
              dataSourceName
              bankAccountNumber
              sortCode
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<BankAccountViewListResponse>(query, {
    first,
    after,
  });
  return data.bankAccounts.bankAccountViewList;
}

interface BankAccountViewTotalsResponse {
  bankAccounts: {
    bankAccountViewTotals: BankAccountViewTotals;
  };
}

export async function getBankAccountTotals(): Promise<BankAccountViewTotals> {
  const query = `
    query BankAccountViewTotals {
      bankAccounts {
        bankAccountViewTotals {
          accountCount
          totalLedgerBalance
          totalRestricted
          totalAvailableCash
          totalHeadroom
        }
      }
    }
  `;
  const data = await graphqlRequest<BankAccountViewTotalsResponse>(query);
  return data.bankAccounts.bankAccountViewTotals;
}

interface RestrictedFundViewListResponse {
  bankAccounts: {
    restrictedFundViewList: Connection<RestrictedFundViewNode>;
  };
}

export async function getRestrictedFundsGraphql(
  bankAccountId?: number,
  first?: number,
  after?: string,
): Promise<Connection<RestrictedFundViewNode>> {
  const query = `
    query RestrictedFundViewList($filter: RestrictedFundViewFilterInput, $first: Int, $after: String) {
      bankAccounts {
        restrictedFundViewList(filter: $filter, first: $first, after: $after) {
          edges {
            cursor
            node {
              id
              bankAccountId
              restrictionType
              label
              restrictedAmount
              isLegal
              releaseDate
              noticePeriodDays
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const filter = bankAccountId != null ? { bankAccountId } : undefined;
  const data = await graphqlRequest<RestrictedFundViewListResponse>(query, {
    filter,
    first,
    after,
  });
  return data.bankAccounts.restrictedFundViewList;
}

interface CreditFacilityViewListResponse {
  bankAccounts: {
    creditFacilityViewList: Connection<CreditFacilityViewNode>;
  };
}

export async function getCreditFacilitiesGraphql(
  bankAccountId?: number,
  first?: number,
  after?: string,
): Promise<Connection<CreditFacilityViewNode>> {
  const query = `
    query CreditFacilityViewList($filter: CreditFacilityViewFilterInput, $first: Int, $after: String) {
      bankAccounts {
        creditFacilityViewList(filter: $filter, first: $first, after: $after) {
          edges {
            cursor
            node {
              id
              bankAccountId
              type
              facilityLimit
              facilityDrawn
              facilityRate
              facilityReviewDate
              securedOn
              headroom
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const filter = bankAccountId != null ? { bankAccountId } : undefined;
  const data = await graphqlRequest<CreditFacilityViewListResponse>(query, {
    filter,
    first,
    after,
  });
  return data.bankAccounts.creditFacilityViewList;
}
